import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  ArrowRight, CheckCircle, Circle, MagnifyingGlass, Buildings, Phone,
  NotePencil, UserCircle, Briefcase, UsersThree, Kanban,
  ListChecks, FileText, CalendarCheck, Handshake, Receipt, CurrencyGbp
} from "@phosphor-icons/react";

const OF_ICONS = [MagnifyingGlass, Buildings, Phone, NotePencil, UserCircle, Briefcase, UsersThree, ListChecks, Kanban];
const CF_ICONS = [Briefcase, MagnifyingGlass, ListChecks, FileText, CalendarCheck, Handshake, CheckCircle, Receipt, CurrencyGbp];

function WorkflowStep({ step, index, icon: Icon, isActive, isComplete, total }) {
  return (
    <div className={`flex items-start gap-3 py-3 px-3 rounded-lg transition-colors ${isComplete ? "bg-green-50" : isActive ? "bg-[#F0F4FF]" : "bg-white"}`}>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
        isComplete ? "bg-green-500 text-white" : isActive ? "bg-[#0F172A] text-white" : "bg-[#F3F4F6] text-[#9CA3AF]"
      }`}>
        {isComplete ? <CheckCircle size={16} weight="bold" /> : <Icon size={16} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-[#9CA3AF] uppercase">Step {index + 1}</span>
          {isComplete && <span className="text-[10px] font-medium text-green-600">Complete</span>}
        </div>
        <p className={`text-sm font-medium ${isComplete ? "text-green-800" : "text-[#0F172A]"}`}>{step.label}</p>
        <p className="text-xs text-[#6B7280] mt-0.5">{step.description}</p>
      </div>
      {index < total - 1 && (
        <ArrowRight size={14} className="text-[#D1D5DB] flex-shrink-0 mt-2" />
      )}
    </div>
  );
}

function DailyProgress({ progress, targets }) {
  const metrics = [
    { key: "calls_made", label: "Calls Made", target: targets?.calls || 50 },
    { key: "companies_added", label: "Companies Added", target: targets?.companies_added || 10 },
    { key: "vacancies_created", label: "Vacancies Created", target: targets?.vacancies_created || 2 },
    { key: "leads_generated", label: "Leads Generated", target: targets?.leads_generated || 5 },
    { key: "emails_sent", label: "Emails Sent", target: targets?.emails_sent || 10 },
  ];

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="daily-progress-panel">
      <h3 className="text-sm font-semibold text-[#0F172A] mb-3">Daily Progress</h3>
      <div className="space-y-3">
        {metrics.map(m => {
          const actual = progress?.[m.key] || 0;
          const pct = Math.min(100, Math.round((actual / m.target) * 100));
          return (
            <div key={m.key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-[#6B7280]">{m.label}</span>
                <span className="text-xs font-medium text-[#0F172A]">{actual} / {m.target}</span>
              </div>
              <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-green-500" : pct >= 50 ? "bg-[#C5A059]" : "bg-[#0F172A]"}`}
                  style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function WorkflowPage() {
  const { user } = useAuth();
  const [steps, setSteps] = useState({ opportunity_finder: [], candidate_finder: [] });
  const [progress, setProgress] = useState(null);
  const [targets, setTargets] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [stepsRes, progressRes, targetsRes] = await Promise.all([
          api.get("/workflows/steps"),
          api.get("/workflows/progress"),
          api.get("/daily-targets"),
        ]);
        setSteps(stepsRes.data);
        setProgress(progressRes.data);
        setTargets(targetsRes.data?.targets || targetsRes.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return (
    <div className="space-y-4">{[1,2,3].map(i => <div key={i} className="h-24 skeleton-shimmer rounded-lg" />)}</div>
  );

  const role = user?.role || "opportunity_finder";
  const showOF = role === "admin" || role === "team_leader" || role === "opportunity_finder";
  const showCF = role === "admin" || role === "team_leader" || role === "candidate_finder";

  return (
    <div data-testid="workflow-page" className="space-y-6">
      <div>
        <h1 className="text-xl md:text-2xl font-bold text-[#0F172A]">Recruitment Workflow</h1>
        <p className="text-sm text-[#6B7280] mt-0.5">Follow the guided steps for successful placements</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-5">
          {/* Opportunity Finder Workflow */}
          {showOF && steps.opportunity_finder.length > 0 && (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="of-workflow">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#0F172A] flex items-center justify-center">
                  <MagnifyingGlass size={16} className="text-[#C5A059]" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-[#0F172A]">Opportunity Finder Workflow</h2>
                  <p className="text-[10px] text-[#6B7280]">Business development pipeline</p>
                </div>
              </div>
              <div className="space-y-1">
                {steps.opportunity_finder.map((step, i) => (
                  <WorkflowStep key={step.key} step={step} index={i} icon={OF_ICONS[i] || Circle}
                    isActive={false} isComplete={false} total={steps.opportunity_finder.length} />
                ))}
              </div>
            </div>
          )}

          {/* Candidate Finder Workflow */}
          {showCF && steps.candidate_finder.length > 0 && (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="cf-workflow">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-[#C5A059] flex items-center justify-center">
                  <UsersThree size={16} className="text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-[#0F172A]">Candidate Finder Workflow</h2>
                  <p className="text-[10px] text-[#6B7280]">Candidate sourcing to placement</p>
                </div>
              </div>
              <div className="space-y-1">
                {steps.candidate_finder.map((step, i) => (
                  <WorkflowStep key={step.key} step={step} index={i} icon={CF_ICONS[i] || Circle}
                    isActive={false} isComplete={false} total={steps.candidate_finder.length} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Daily Progress Sidebar */}
        <div>
          <DailyProgress progress={progress} targets={targets} />
        </div>
      </div>
    </div>
  );
}
